/**
 * Random utilities for Remotion video templates
 *
 * Provides seeded random number generation for deterministic,
 * reproducible animations across renders.
 */

/**
 * Creates a seeded random number generator for reproducible results.
 *
 * Uses a linear congruential generator (LCG) algorithm to produce
 * deterministic sequences of pseudo-random numbers from a given seed.
 * This ensures animations appear identical across renders.
 *
 * @param seed - Initial seed value for the generator
 * @returns A function that returns the next random number (0-1) each time called
 *
 * @example
 * const random = seededRandom(12345);
 * const value1 = random(); // Always produces the same sequence
 * const value2 = random();
 */
export function seededRandom(seed: number): () => number {
  let state = seed;
  return (): number => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Generates a seeded random number within a specified range.
 *
 * Convenience function that combines seededRandom with range mapping.
 * Note: Creates a new generator each call - for multiple values from
 * the same sequence, use seededRandom() directly.
 *
 * @param seed - Seed value for reproducibility
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (exclusive)
 * @returns A random number between min and max
 *
 * @example
 * const size = seededRandomRange(42, 10, 50); // Random size between 10-50
 */
export function seededRandomRange(seed: number, min: number, max: number): number {
  const random = seededRandom(seed);
  return min + random() * (max - min);
}

/**
 * Creates a seeded random generator that produces values within a range.
 *
 * More efficient than seededRandomRange for generating multiple values,
 * as it reuses the same generator instance.
 *
 * @param seed - Initial seed value
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (exclusive)
 * @returns A function that returns random numbers in the specified range
 *
 * @example
 * const randomSize = createSeededRandomRange(12345, 20, 100);
 * const size1 = randomSize(); // 20-100
 * const size2 = randomSize(); // 20-100, different value
 */
export function createSeededRandomRange(
  seed: number,
  min: number,
  max: number
): () => number {
  const random = seededRandom(seed);
  return (): number => min + random() * (max - min);
}
